import { prop } from 'ramda';
import { useMemo } from 'react';
import { useAppSelector } from 'js/store/hooks';
import {
    useParcelJoin,
    useParcelLeave,
    useParcelSubscribeDetails,
    useParcelSubscribePossession,
    useParcelSubscribeDiscoveryPage,
    useLeaderBoardJoin,
    useLeaderBoardLeave,
    useLeaderBoardGetDetails,
    useLeaderBoardGetActiveList,
    useLeaderBoardSubscribeDetails,
    useLeaderBoardSubscribeSummary,
    useLeaderBoardGetHistoricalList,
    useLeaderBoardUnsubscribeDetails,
    useLeaderBoardUnsubscribeSummary
} from 'js/hooks/socket';

export const useLeaderboardPublicAPI = () => {
    const joinLeaderboard = useLeaderBoardJoin();
    const leaveLeaderboard = useLeaderBoardLeave();
    const getLeaderboardDetails = useLeaderBoardGetDetails();
    const getActiveLeaderboards = useLeaderBoardGetActiveList();
    const subscribeLeaderboardDetails = useLeaderBoardSubscribeDetails();
    const subscribeLeaderboardSummary = useLeaderBoardSubscribeSummary();
    const getHistoricalLeaderboards = useLeaderBoardGetHistoricalList();
    const unsubscribeLeaderboardDetails = useLeaderBoardUnsubscribeDetails();
    const unsubscribeLeaderboardSummary = useLeaderBoardUnsubscribeSummary();
    const joinParcel = useParcelJoin();
    const leaveParcel = useParcelLeave();
    const subscribeParcelDetails = useParcelSubscribeDetails();
    const subscribeParcelDiscoveryPage = useParcelSubscribeDiscoveryPage();
    const subscribeParcelPossession = useParcelSubscribePossession();
    const { displayPlayer, networkId, networkType, enableDemoMode } = useAppSelector(prop('leaderboard'));

    // todo write types for CMS API
    return useMemo(
        () => ({
            /**
             * Gets the leaderboard configuration.
             * @memberOf Playtech.API.leaderboard
             * @function
             * @returns {{
             *  displayPlayer: string,
             *  networkId: string,
             *  enableDemoMode: boolean,
             *  networkType: string
             * }}
             * @example
             * Playtech.API.leaderboard.getLeaderBoardConfiguration();
             */
            getLeaderBoardConfiguration: () => ({
                displayPlayer,
                enableDemoMode,
                networkId,
                networkType
            }),
            /**
             * Join player to leaderboard.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            joinLeaderboard,
            /**
             * Leave player from leaderboard.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            leaveLeaderboard,
            /**
             * Get leaderboard details.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            getLeaderboardDetails,
            /**
             * Get player's active leaderboards list.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            getActiveLeaderboards,
            /**
             * Subscribe for leaderboard details notifications.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            subscribeLeaderboardDetails,
            /**
             * Subscribe for leaderboard summary notifications.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            subscribeLeaderboardSummary,
            /**
             * Get player's historical leaderboards list.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            getHistoricalLeaderboards,
            /**
             * Unsubscribe from leaderboard details notifications.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            unsubscribeLeaderboardDetails,
            /**
             * Unsubscribe from leaderboard summary notifications.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            unsubscribeLeaderboardSummary,
            /**
             * Join player to parcel.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            joinParcel,
            /**
             * Leave player from parcel.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            leaveParcel,
            /**
             * Subscribe for parcel details notifications.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            subscribeParcelDetails,
            /**
             * Subscribe for parcel discovery page notifications.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            subscribeParcelDiscoveryPage,
            /**
             * Subscribe for parcel possession notifications.
             * @memberOf Playtech.API.leaderboard
             * @function
             */
            subscribeParcelPossession
        }),
        [
            networkId,
            joinParcel,
            leaveParcel,
            networkType,
            displayPlayer,
            enableDemoMode,
            joinLeaderboard,
            leaveLeaderboard,
            getActiveLeaderboards,
            getLeaderboardDetails,
            subscribeParcelDetails,
            subscribeParcelPossession,
            getHistoricalLeaderboards,
            subscribeLeaderboardDetails,
            subscribeLeaderboardSummary,
            subscribeParcelDiscoveryPage,
            unsubscribeLeaderboardDetails,
            unsubscribeLeaderboardSummary
        ]
    );
};
